/**
 * /features — landing-style index that lists every feature, grouped by
 * category. Each card links to its dedicated /features/:slug page.
 *
 * Same vibe as the "Características" section of the home, but here it's a
 * full standalone page that can rank in Google and serve as a hub for the
 * megamenu's "Ver todas las features →" link.
 */
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { SiteFooter } from '../components/SiteFooter';
import { getFeaturesByCategory } from '../lib/features';

export function Features() {
  useEffect(() => {
    const prev = document.title;
    document.title = 'Todas las features · Zettapost';
    return () => {
      document.title = prev;
    };
  }, []);

  const grouped = getFeaturesByCategory();

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <Navbar />

      <section className="relative overflow-hidden bg-gradient-to-br from-white via-brand-50/40 to-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 py-14 lg:py-20 text-center">
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-brand-700">
            <Sparkles size={12} />
            Todo lo que puedes hacer
          </span>
          <h1 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight">
            Todo lo que necesitas para crecer en tus redes
          </h1>
          <p className="mt-5 text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto">
            Programa y publica en 9 redes, crea contenido con inteligencia artificial, mide tus resultados y trabaja en equipo. Todo desde un mismo sitio, en español y sin complicaciones.
          </p>
        </div>
      </section>

      {grouped.map(({ category, features }) =>
        features.length === 0 ? null : (
          <section
            key={category.id}
            className="border-b border-slate-200 last:border-b-0"
          >
            <div className="max-w-6xl mx-auto px-6 lg:px-8 py-14 lg:py-20">
              <div className="flex items-baseline justify-between flex-wrap gap-2">
                <div>
                  <span className="text-xs font-semibold uppercase tracking-[0.18em] text-brand-700">
                    {category.short}
                  </span>
                  <h2 className="mt-1 text-2xl lg:text-3xl font-bold text-slate-900">
                    {category.label}
                  </h2>
                </div>
                <span className="text-sm text-slate-400">{features.length} features</span>
              </div>

              <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {features.map((f) => {
                  const Icon = f.icon;
                  return (
                    <Link
                      key={f.slug}
                      to={`/features/${f.slug}`}
                      className="group rounded-2xl border border-slate-200 bg-white p-5 hover:border-brand-300 hover:shadow-md transition"
                    >
                      <div className="flex items-center justify-between">
                        <div className="h-10 w-10 rounded-lg bg-brand-50 text-brand-700 inline-flex items-center justify-center">
                          <Icon size={18} />
                        </div>
                        {f.badge && (
                          <span className="text-[10px] font-bold tracking-wider px-1.5 py-0.5 rounded bg-gradient-to-r from-amber-500 to-pink-500 text-white">
                            {f.badge}
                          </span>
                        )}
                      </div>
                      <div className="mt-3 text-[11px] font-semibold uppercase tracking-wider text-brand-600">
                        {f.eyebrow}
                      </div>
                      <h3 className="mt-1 font-semibold text-slate-900 text-base group-hover:text-brand-700 transition">
                        {f.title}
                      </h3>
                      <p className="mt-1.5 text-sm text-slate-500 leading-relaxed">
                        {f.shortDescription}
                      </p>
                      <span className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-brand-600 group-hover:text-brand-700">
                        Conocer más
                        <ArrowRight size={13} className="transition group-hover:translate-x-0.5" />
                      </span>
                    </Link>
                  );
                })}
              </div>
            </div>
          </section>
        ),
      )}
      <SiteFooter />
    </div>
  );
}
